export const SOURCE_TYPES = ['url', 'sitemap', 'file', 'text'] as const;
export type SourceType = (typeof SOURCE_TYPES)[number];

export const SOURCE_STATUSES = ['pending', 'crawling', 'indexing', 'ready', 'error', 'deleting'] as const;
export type SourceStatus = (typeof SOURCE_STATUSES)[number];

/** How a `crawl_source` job run ended (source-detail-diagnostics spec) — read this off a
 * `CrawlRun` row directly, never re-derived from `DataSource.error` (a parsed error string
 * can't be trusted to name the right cause; this enum is the thing that's actually true).
 * Review round 1 (2026-08-10) caught this doc comment itself mismapping `truncated_capacity`
 * to "the plan's page budget" — corrected below; get these wrong and Task 3's screen copy
 * tells an owner to upgrade their PLAN for a problem that is entirely OUR Cloudflare capacity.
 *
 *   - `complete`            — ran to the end of the frontier (or the sitemap).
 *   - `truncated_capacity`  — OUR Cloudflare rendering capacity ran out (CF's `2001` rate
 *                             limit outlasted the backoff budget). Never the customer's site,
 *                             never their plan.
 *   - `truncated_deadline`  — OUR whole-crawl wall-clock ceiling elapsed before the frontier
 *                             emptied.
 *   - `truncated_attempts`  — burned the crawl's attempt budget (successes + failures,
 *                             independent of `maxPages`) before the frontier emptied —
 *                             typically a site with many dead/erroring links.
 *   - `truncated_page_cap`  — hit the WORKSPACE's plan page cap with pages still left to
 *                             crawl. Distinct from a site that happens to have exactly that
 *                             many pages and nothing more — that case is `complete`.
 *   - `failed`              — the job threw, or the crawl emitted zero pages with no more
 *                             specific explanation than that. */
export const CRAWL_OUTCOMES = [
  'complete',
  'truncated_capacity',
  'truncated_deadline',
  'truncated_attempts',
  'truncated_page_cap',
  'failed',
] as const;
export type CrawlOutcome = (typeof CRAWL_OUTCOMES)[number];

export const JOB_KINDS = [
  'provision_instance',
  'crawl_source',
  'index_documents',
  'delete_source_docs',
  'delete_instance',
  'push_usage',
  'first_answer_notify',
  // public-playground Task 3 — `demo_crawl` is the slow-lane counterpart of `crawl_source`
  // (`wiring.ts`'s `SLOW_JOB_KINDS`); `demo_reap` is fast-lane by being absent from that
  // list, same as every other kind below `first_answer_notify`.
  'demo_crawl',
  'demo_reap',
  // index-verification design (2026-08-14) — chained after `index_documents`, fast-lane
  // (absent from `SLOW_JOB_KINDS`, same convention as `demo_reap` above).
  'verify_indexing',
  // feedback-form design (2026-08-18) — sends the FEEDBACK_INBOX notification email after a
  // Feedback row is created (routes/feedback.ts). Fast-lane, same convention as above.
  'feedback_notify',
  // workspace-members cycle (THI-110 Task 3) — sends the invite email after a WorkspaceInvite
  // row is created or replaced (routes/members.ts). Fast-lane, same convention as above.
  'invite_notify',
  // bot-notifications design (2026-08-21) Task 2 — sends the newLead email after a Lead row is
  // created (never on the fill-missing update branch). Fast-lane, same convention as above.
  'lead_notify',
  // bot-notifications design (2026-08-21) Task 2 — sends the crawlFailed email the first time
  // a source transitions INTO `error`; not re-sent while it stays there (`DataSource.
  // errorNotifiedAt`). Fast-lane, same convention as above.
  'source_error_notify',
  // bot-notifications design (2026-08-21) Task 3 — the ONE self-rescheduling daily digest job,
  // `push_usage`'s idiom exactly (`ensureDigestScheduled()` on poller start, 08:00 UTC,
  // reschedule in `finally`). Fast-lane, same convention as above.
  'notification_digest',
] as const;
export type JobKind = (typeof JOB_KINDS)[number];

/** `Document.indexState` (index-verification design, 2026-08-14) — whether Cloudflare AI
 * Search has actually finished indexing an uploaded document, as opposed to merely having
 * accepted the upload (`Document.indexedAt`, set the moment CF's HTTP call returns 2xx).
 * `verify_indexing` is the only job that ever writes `error`; every other write keeps the
 * `@default(pending)` until verification runs. */
export const INDEX_STATES = ['pending', 'indexed', 'error'] as const;
export type IndexState = (typeof INDEX_STATES)[number];

/** `Document.indexError` codes that `verify_indexing` treats as PERMANENT — retrying via
 * `RetrievalProvider.resyncDoc` cannot fix these, so the job persists them immediately
 * instead of spending the one-resync-per-chain budget. Any other CF error code (including
 * `item_missing`, our own code for "we uploaded it but CF has no row for it") is treated as
 * transient and gets exactly one resync attempt first. Not a DB enum — `indexError` stores
 * whatever code Cloudflare (or we) attach; this list is a job-level classification of it. */
export const PERMANENT_INDEX_ERROR_CODES = [
  'unsupported_type',
  'file_not_found',
  'invalid_url',
  'file_is_corrupt',
  'over_size',
] as const;

export const JOB_STATUSES = ['pending', 'running', 'done', 'failed'] as const;
export type JobStatus = (typeof JOB_STATUSES)[number];

export const MEMBER_ROLES = ['owner', 'admin', 'member'] as const;
export type MemberRole = (typeof MEMBER_ROLES)[number];

export const PLAN_CODES = ['free', 'starter', 'growth'] as const;
export type PlanCode = (typeof PLAN_CODES)[number];

export const BOT_STATUSES = ['active', 'paused'] as const;
export type BotStatus = (typeof BOT_STATUSES)[number];

/** `DemoBot.status` (public-playground spec) — a stranger's URL becomes a real, short-lived
 * bot with no signup. Distinct from `SourceStatus`: a demo has exactly one source (the pasted
 * URL) and one lifecycle, crawl and index collapsed into the same row rather than a
 * `DataSource` + `Bot` pair. `expired` is terminal and reached only by the `demo_reap` job,
 * never by the crawl path itself.
 *
 * `needs_js` (Task 3) — a demo whose static crawl completed but came back near-empty: the
 * site most likely renders its content with client-side JavaScript, which the playground's
 * static-only crawl can't see (rendered mode stays off for the playground, see the design
 * spec's "Constraints, measured"). A STATUS, not a prose string parsed out of `DemoBot.error`
 * — Task 5's UI branches on it to show the "sign up to try rendered mode" CTA, and doing that
 * off an English sentence would be exactly the kind of hand-parsed convention `HINT_PREFIX`
 * exists to avoid for `DataSource.error`. Distinct from `error`: a genuine crawl failure
 * (nothing reachable, zero pages) is `error`; a crawl that succeeded but found the site is a
 * JS shell is `needs_js` — the two need different UI treatment. */
export const DEMO_STATUSES = [
  'pending',
  'crawling',
  'indexing',
  'ready',
  'error',
  'needs_js',
  'expired',
] as const;
export type DemoStatus = (typeof DEMO_STATUSES)[number];

/** `Conversation.channel` (dashboard-shell-feedback fix-wave item 9) — the DB column is a
 * plain `String @default("web")`, not a Postgres enum (channel adapters beyond the web widget
 * are post-MVP, per this repo's `ChannelAdapter` architecture), but exactly two values are
 * ever written today: `routes/public-chat.ts` writes `'web'`, `routes/playground.ts` writes
 * `'playground'`. Narrowing the DTOs to this union — instead of bare `string` — turns a typo
 * in a `=== 'playground'` frontend comparison into a type error rather than a silently-false
 * check. Widening this when a real second channel lands is a one-line addition here, same
 * shape as `SourceStatus`/`STATUS_SEVERITY` above. */
export const CONVERSATION_CHANNELS = ['web', 'playground'] as const;
export type ConversationChannel = (typeof CONVERSATION_CHANNELS)[number];

export const SUBSCRIPTION_STATUSES = ['active', 'past_due', 'canceled'] as const;
export type SubscriptionStatus = (typeof SUBSCRIPTION_STATUSES)[number];

export const POST_STATUSES = ['draft', 'published'] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

/** `UnansweredQuestion.status` (triage-state design, 2026-08-21 §A) — mirrors the Prisma
 * `UnansweredStatus` enum (`packages/db/prisma/schema.prisma`). Kept as its own catalog here
 * (same "shared-types owns the wire-level union, Prisma owns the column" split every other
 * enum in this file uses) rather than importing the generated Prisma type into DTOs/Zod
 * schemas that must not depend on `@repo/db`. `answered` is set exclusively by `POST
 * /unanswered/answer` (`markAnswered`) — never settable via the `PATCH .../unanswered/:id`
 * route, which only ever writes `open`/`dismissed`. */
export const UNANSWERED_STATUSES = ['open', 'answered', 'dismissed'] as const;
export type UnansweredStatus = (typeof UNANSWERED_STATUSES)[number];

/** `Lead.status` (triage-state design, 2026-08-21 §B) — mirrors the Prisma `LeadStatus` enum
 * (`packages/db/prisma/schema.prisma`), same split as `UnansweredStatus` above. Unlike
 * `UnansweredStatus`, every value here is settable by hand (`PATCH .../leads/:leadId`,
 * `POST .../leads/bulk-status`) — there is no write-restricted member the way `answered` is. */
export const LEAD_STATUSES = ['new', 'contacted', 'archived'] as const;
export type LeadStatus = (typeof LEAD_STATUSES)[number];

/** `BotNotificationPref.unansweredDigest` (bot-notifications design, 2026-08-21 §Data) —
 * mirrors the Prisma `DigestCadence` enum (`packages/db/prisma/schema.prisma`), same
 * shared-types-owns-the-wire-union split every other enum in this file uses. `off` means the
 * digest email never sends for that (bot, user) pair; `daily`/`weekly` pick which
 * `notification_digest` run recipients them. */
export const DIGEST_CADENCES = ['off', 'daily', 'weekly'] as const;
export type DigestCadence = (typeof DIGEST_CADENCES)[number];

/** Notification event keys (bot-notifications design, 2026-08-21 §Events) — the four
 * configurable per-(bot, user) notification toggles. `firstAnswer` is deliberately NOT a
 * member: it stays the existing owner-only, unconfigurable `first_answer_notify` job and has
 * no `BotNotificationPref` field. Used as `NotificationPrefsDto`'s key set and as the `event`
 * parameter to `getRecipientsUnscoped` (`repos/notification-prefs.ts`). */
export const NOTIFICATION_EVENTS = ['newLead', 'unansweredDigest', 'crawlFailed', 'weeklySummary'] as const;
export type NotificationEvent = (typeof NOTIFICATION_EVENTS)[number];

/** Statuses that keep a workspace entitled to its paid plan (M6 fix-wave SEV-1 #2):
 * `past_due` means Polar is still retrying a failed charge, not that access should
 * immediately collapse to `free` mid-dunning. Shared by `apps/api`'s `getWorkspacePlan`
 * (lib/plan.ts), `packages/db`'s `billing-view.ts` (`currentPlan`), and
 * `crawl-source.ts`'s `resolveRemainingPageBudget` so every entitlement check agrees. */
export const ENTITLED_SUBSCRIPTION_STATUSES = ['active', 'past_due'] as const;
